/** Account-free chat with conversation history saved in this browser. */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { isQwenRoomEnabled } from '../../services/qwenRoomConfig';
import { useStore } from '../../store';
import { useChat } from '../../hooks/useChat';
import { useConnection } from '../../hooks/useConnection';
import { Sidebar } from '../sidebar/Sidebar';
import { MessageList } from '../chat/MessageList';
import { MessageInput } from '../chat/MessageInput';
import { EmptyState, DailyLimitNotice } from './EmptyState';
import { ConfirmModal } from '../modals/ConfirmModal';
import { InfoModal, type InfoVariant } from '../modals/InfoModal';
import { toastManager } from '../notifications/ToastProvider';
import { getGuestRequestHeaders } from '../../utils/guestIdentity';
import { loadGuestChats, saveGuestChats } from '../../utils/guestChatStorage';
import { trackMarketingEvent, trackTrinityPageViewOnce } from '../../services/marketingAnalytics';
import styles from '../../styles/components/AppShell.module.css';
import type { ChatMessage } from '../../types';

const buildGuestHeaders = async () => getGuestRequestHeaders();
const QwenRoom = lazy(() => import('../qwen/QwenRoom'));

export function AppShell() {
  const qwenRoomEnabled = isQwenRoomEnabled();
  const [qwenRoomActive, setQwenRoomActive] = useState(() =>
    qwenRoomEnabled && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('view') === 'qwen-room'
  );
  const openQwenRoom = (active: boolean) => {
    setQwenRoomActive(active);
    const url = new URL(window.location.href);
    if (active) url.searchParams.set('view', 'qwen-room');
    else url.searchParams.delete('view');
    window.history.replaceState(null, '', url);
  };
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 640);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [infoVariant, setInfoVariant] = useState<InfoVariant | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [storageReady, setStorageReady] = useState(false);
  const transcripts = useRef(new Map<string, ChatMessage[]>());
  const storageWarningShown = useRef(false);
  const sendInFlight = useRef(false);
  const chatHistory = useStore((state) => state.chatHistory);
  const chatStarted = useStore((state) => state.chatStarted);
  const currentChatId = useStore((state) => state.currentChatId);
  const allChats = useStore((state) => state.allChats);
  const isGenerating = useStore((state) => state.isGenerating);
  const chat = useChat();
  const { status: connectionStatus } = useConnection();
  const cooldownSeconds = Math.max(0, Math.ceil((chat.cooldownUntil - now) / 1000));
  const limitReached = chat.allowance?.remaining === 0;

  useEffect(() => { trackTrinityPageViewOnce(); }, []);

  useEffect(() => {
    const saved = loadGuestChats();
    if (saved) {
      transcripts.current = new Map(saved.transcripts);
      const messages = saved.currentChatId ? transcripts.current.get(saved.currentChatId) || [] : [];
      useStore.setState({
        allChats: saved.chats,
        currentChatId: saved.currentChatId,
        chatHistory: messages,
        contextMemory: messages.slice(-useStore.getState().CONTEXT_WINDOW_SIZE),
        chatStarted: messages.length > 0,
      });
    }
    setStorageReady(true);
  }, []);

  useEffect(() => {
    if (!storageReady) return;
    const state = useStore.getState();
    if (state.currentChatId && state.chatHistory.length) {
      transcripts.current.set(state.currentChatId, [...state.chatHistory]);
    }
    const saved = saveGuestChats({
      version: 1,
      currentChatId: state.currentChatId,
      chats: state.allChats,
      transcripts: [...transcripts.current.entries()],
    });
    if (!saved && !storageWarningShown.current) {
      storageWarningShown.current = true;
      toastManager.error('Browser storage is unavailable or full. Export chats you want to keep before closing this page.');
    }
  }, [storageReady, chatHistory, currentChatId, allChats]);

  useEffect(() => {
    void chat.refreshAllowance(buildGuestHeaders);
    const timer = window.setInterval(() => void chat.refreshAllowance(buildGuestHeaders), 30_000);
    return () => window.clearInterval(timer);
  }, [chat.refreshAllowance]);

  useEffect(() => {
    setNow(Date.now());
    if (chat.cooldownUntil <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [chat.cooldownUntil]);

  const saveCurrentChat = useCallback(() => {
    const state = useStore.getState();
    if (!state.currentChatId || !state.chatHistory.length) return;
    const id = state.currentChatId;
    transcripts.current.set(id, [...state.chatHistory]);
    const existing = state.allChats.find((item) => item.chatId === id);
    const firstPrompt = state.chatHistory.find((message) => message.role === 'user')?.content || 'New Chat';
    const item = {
      chatId: id,
      title: existing?.title || firstPrompt.replace(/\s+/g, ' ').slice(0, 55),
      messageCount: state.chatHistory.length,
      createdAt: existing?.createdAt ?? Date.now(),
      lastUpdated: Date.now(),
      pinned: existing?.pinned ?? false,
    };
    state.setAllChats([item, ...state.allChats.filter((other) => other.chatId !== id)]);
  }, []);

  const handleSend = useCallback(async (message: string, attachment?: File) => {
    if (sendInFlight.current || chat.cooldownUntil > Date.now() || chat.allowance?.remaining === 0) return;
    sendInFlight.current = true;
    setIsSending(true);
    try {
      let prompt = message;
      if (attachment) prompt = `[Attached file: ${attachment.name}]\n\n${await attachment.text()}\n\n${message}`;
      const state = useStore.getState();
      if (!state.currentChatId) state.setCurrentChatId(state.generateChatId());
      state.addMessage('user', prompt);
      saveCurrentChat();
      const result = await chat.send(prompt, buildGuestHeaders);
      const tokens = chat.getTokens();
      if (tokens) useStore.getState().addMessage('assistant', tokens);
      if (result.success) {
        trackMarketingEvent('trinity_response');
      } else if (result.error && result.error !== 'Aborted') {
        toastManager.error(result.error);
      }
      saveCurrentChat();
    } catch (error) {
      toastManager.error(error instanceof Error ? error.message : 'Unable to send your message.');
    } finally {
      sendInFlight.current = false;
      setIsSending(false);
      void chat.refreshAllowance(buildGuestHeaders);
    }
  }, [chat, saveCurrentChat]);

  const handleNewChat = () => {
    if (sendInFlight.current) return;
    saveCurrentChat();
    useStore.getState().reset();
  };

  const handleLoadChat = (id: string) => {
    if (sendInFlight.current) return;
    const messages = transcripts.current.get(id);
    if (!messages) return;
    saveCurrentChat();
    const state = useStore.getState();
    useStore.setState({
      currentChatId: id,
      chatHistory: [...messages],
      contextMemory: messages.slice(-state.CONTEXT_WINDOW_SIZE),
      chatStarted: messages.length > 0,
    });
  };

  const handleDeleteChat = () => {
    if (!deleteTarget || sendInFlight.current) return;
    const state = useStore.getState();
    transcripts.current.delete(deleteTarget);
    if (state.currentChatId === deleteTarget) state.reset();
    state.setAllChats(state.allChats.filter((item) => item.chatId !== deleteTarget));
    setDeleteTarget(null);
  };

  const handleExportChat = (id: string) => {
    saveCurrentChat();
    const messages = transcripts.current.get(id);
    if (!messages) return;
    const title = useStore.getState().allChats.find((item) => item.chatId === id)?.title || 'W AI chat';
    const markdown = [`# ${title}`, ...messages.map((message) => `### ${message.role === 'user' ? 'You' : 'W AI'}\n\n${message.content}`)].join('\n\n');
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/[^a-zA-Z0-9-_ ]/g, '')}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleEdit = (index: number, content: string) => {
    if (sendInFlight.current || chat.cooldownUntil > Date.now() || limitReached) return;
    const state = useStore.getState();
    const messages = state.chatHistory.slice(0, index);
    state.setChatHistory(messages);
    state.setContextMemory(messages.slice(-state.CONTEXT_WINDOW_SIZE));
    void handleSend(content);
  };

  return (
    <div className={styles.container}>
      {sidebarOpen && (
        <div className={styles.sidebar}>
          <Sidebar
            chats={allChats}
            currentChatId={currentChatId}
            connectionStatus={connectionStatus}
            isBusy={isSending || isGenerating || chat.isStreaming}
            allowance={chat.allowance}
            onNewChat={() => { openQwenRoom(false); handleNewChat(); }}
            onLoadChat={(id) => { openQwenRoom(false); handleLoadChat(id); }}
            onOpenQwenRoom={qwenRoomEnabled ? () => openQwenRoom(true) : undefined}
            onOpenMainChat={() => openQwenRoom(false)}
            qwenRoomActive={qwenRoomActive}
            onDeleteChat={setDeleteTarget}
            onPinChat={(id) => {
              const state = useStore.getState();
              state.setAllChats(state.allChats.map((item) => item.chatId === id ? { ...item, pinned: !item.pinned } : item));
            }}
            onExportChat={handleExportChat}
            onShowInfo={setInfoVariant}
          />
        </div>
      )}
      <button
        type="button"
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        onClick={() => setSidebarOpen((previous) => !previous)}
        style={{ position: 'absolute', top: '16px', left: sidebarOpen ? '296px' : '16px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', width: '32px', height: '32px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', zIndex: 10 }}
      >
        {sidebarOpen ? '\u2190' : '\u2192'}
      </button>
      <div className={styles.main}>
        {qwenRoomActive ? (
          <Suspense fallback={<div role="status">Opening the Qwen Room…</div>}><QwenRoom /></Suspense>
        ) : <>
        {!chatStarted && !chat.isStreaming ? <EmptyState /> : (
          <MessageList messages={chatHistory} streamingTokens={chat.tokens} isStreaming={chat.isStreaming} phase={chat.phase} onEdit={handleEdit} />
        )}
        <DailyLimitNotice allowance={chat.allowance} />
        <div className={styles.inputArea}>
          {cooldownSeconds > 0 && !limitReached && (
            <p role="status" style={{ textAlign: 'center', color: 'var(--color-text-muted)', marginBottom: '8px', fontSize: '0.875rem' }}>
              Please wait {cooldownSeconds}s before sending another message.
            </p>
          )}
          <MessageInput
            onSend={(message, attachment) => void handleSend(message, attachment)}
            onStop={chat.stop}
            isGenerating={isSending || isGenerating || chat.isStreaming}
            disabled={!connectionStatus.connected || limitReached || cooldownSeconds > 0}
            placeholder={limitReached ? 'Your daily messages will renew automatically.' : 'Ask anything...'}
          />
        </div>
        </>}
      </div>
      {deleteTarget && (
        <ConfirmModal title="Remove Chat" message="Remove this conversation from this browser?" confirmLabel="Remove" destructive onConfirm={handleDeleteChat} onCancel={() => setDeleteTarget(null)} />
      )}
      {infoVariant && (
        <InfoModal variant={infoVariant} data={{ model: connectionStatus.model, gpu_type: connectionStatus.gpuType, provider: connectionStatus.provider }} onClose={() => setInfoVariant(null)} />
      )}
    </div>
  );
}

export default AppShell;
